import { isEmpty, sortBy } from 'lodash-es';
import { GeoCellDisplayStatus, GeoTestStatus } from '../models/ICPAdTestConfig';
import { GeoTestType, IColumnIcon } from '@libs/apis';
import { IGeoTestMetricDefinition } from '@portal/app/geo-designer/models/IGeoTestMetricDefinition';

export const statusFilterOptions = [
  {
    label: 'Active',
    value: GeoTestStatus.active
  },
  {
    label: 'Scheduled',
    value: GeoTestStatus.scheduled
  },
  {
    label: 'Finished',
    value: GeoTestStatus.finished
  }
];

export enum ColumnType {
  text = 'TEXT',
  textWithIcon = 'TEXT_WITH_ICON',
  statusBadge = 'STATUS_BADGE',
  date = 'DATE',
  dateRange = 'DATE_RANGE',
  currency = 'CURRENCY',
  metric = 'METRIC',
  metadata = 'METADATA',
  switch = 'SWITCH',
  percent = 'PERCENT',
  custom = 'CUSTOM',
  boolean = 'BOOLEAN',
  action = 'ACTION',
  loading = 'LOADING'
}

export interface IGeoTestColumnDefinition {
  header: string;
  field: string;
  order: number;
  availableFor: GeoTestStatus[];
  width: string;
  styleClass?: string;
  columnType?: ColumnType;
  isLink?: boolean;
  columnInfo?: string;
  options?: DateRangeProgressOptions;
}

export enum GeoTestBudgetLevel {
  adset = 'adset',
  campaign = 'campaign'
}

export interface IGeoTestMediaFieldDefinition {
  header: string;
  field: string;
  order: number;
  availableFor: GeoTestBudgetLevel[];
  width: string;
  styleClass?: string;
  forTypes: GeoTestType[];
  columnType: ColumnType;
}

interface DateRangeProgressOptions {
  showProgress: boolean;
  startDateField: string;
  endDateField: string;
}

export class GeoTestsFieldDefinition {
  private static readonly geoTestsFieldDefinition: IGeoTestColumnDefinition[] =
    [
      {
        header: 'Test Name',
        field: 'testName',
        order: 1,
        availableFor: [
          GeoTestStatus.active,
          GeoTestStatus.scheduled,
          GeoTestStatus.finished
        ],
        width: '17.5rem',
        styleClass: 'w-full',
        columnType: ColumnType.textWithIcon,
        isLink: true
      },
      {
        header: 'ID',
        field: 'studyId',
        order: 2,
        availableFor: [
          GeoTestStatus.active,
          GeoTestStatus.scheduled,
          GeoTestStatus.finished
        ],
        width: '5rem'
      },
      {
        header: 'New Adjustment Factor',
        field: 'percInc',
        columnType: ColumnType.metric,
        order: 3,
        availableFor: [GeoTestStatus.finished],
        width: '13.75rem',
        styleClass: 'fw-bold'
      },
      {
        header: 'Contribution',
        field: 'percOrdersI',
        columnType: ColumnType.metric,
        order: 4,
        availableFor: [GeoTestStatus.finished],
        width: '10rem',
        styleClass: 'fw-bold',
        columnInfo: 'Contribution to orders over the test duration'
      },
      {
        header: 'Conversion Type',
        field: 'conversionType',
        columnType: ColumnType.metadata,
        order: 5,
        availableFor: [
          GeoTestStatus.active,
          GeoTestStatus.scheduled,
          GeoTestStatus.finished
        ],
        width: '13.75rem'
      },
      {
        header: 'Type',
        field: 'treatment',
        columnType: ColumnType.metadata,
        order: 6,
        availableFor: [
          GeoTestStatus.active,
          GeoTestStatus.scheduled,
          GeoTestStatus.finished
        ],
        width: '11.125rem'
      },
      {
        header: 'Implementation',
        field: 'executionMethod',
        columnType: ColumnType.metadata,
        order: 7,
        availableFor: [
          GeoTestStatus.active,
          GeoTestStatus.scheduled,
          GeoTestStatus.finished
        ],
        width: '10.375rem'
      },
      {
        header: 'Status',
        field: 'displayStatus',
        order: 8,
        availableFor: [GeoTestStatus.active, GeoTestStatus.scheduled],
        width: '9.375rem',
        columnType: ColumnType.statusBadge
      },
      {
        header: 'Test Dates',
        field: 'startDate',
        order: 9,
        availableFor: [GeoTestStatus.active],
        width: '14rem',
        columnType: ColumnType.dateRange,
        options: {
          showProgress: true,
          startDateField: 'startDate',
          endDateField: 'endDate'
        }
      },
      {
        header: 'Test Dates',
        field: 'startDate',
        order: 10,
        availableFor: [GeoTestStatus.scheduled, GeoTestStatus.finished],
        width: '14rem',
        columnType: ColumnType.dateRange,
        options: {
          showProgress: false,
          startDateField: 'startDate',
          endDateField: 'endDate'
        }
      }
    ];

  private static readonly recommendedTestsFieldDefinition: IGeoTestColumnDefinition[] =
    [
      {
        header: 'Tests',
        field: 'name',
        order: 1,
        availableFor: [],
        width: '26.5rem',
        styleClass: 'w-full',
        columnType: ColumnType.textWithIcon,
        isLink: true
      },
      {
        header: 'Type',
        field: 'testType',
        columnType: ColumnType.metadata,
        order: 2,
        availableFor: [],
        width: '11.125rem'
      },
      {
        header: 'Implementation',
        field: 'method',
        columnType: ColumnType.metadata,
        order: 3,
        availableFor: [],
        width: '10.375rem'
      },
      {
        header: 'Scale Cost',
        field: 'additionalSpend',
        order: 4,
        availableFor: [],
        width: '8.325rem',
        columnType: ColumnType.metric,
        columnInfo: 'Cost to run this scale test'
      },
      {
        header: 'Spend',
        field: 'mediaSpend',
        order: 5,
        availableFor: [],
        width: '6.5rem',
        styleClass: 'p-table-column-highlight',
        columnType: ColumnType.metric,
        columnInfo: 'Amount spent over the last 30 days'
      },
      {
        header: 'Contribution',
        field: 'percOrdersI',
        columnType: ColumnType.metric,
        order: 6,
        availableFor: [],
        width: '9.125rem',
        styleClass: 'p-table-column-highlight',
        columnInfo: 'Contribution to orders over the test duration'
      },
      {
        header: 'Incremental ROAS',
        field: 'incrementalRoas',
        columnType: ColumnType.metric,
        order: 7,
        availableFor: [],
        width: '12rem',
        styleClass: 'p-table-column-highlight',
        columnInfo: 'Incremental ROAS over the test duration'
      },
      {
        header: 'Tested',
        field: 'lastTested',
        columnType: ColumnType.date,
        order: 8,
        availableFor: [],
        width: '8.325rem'
      }
    ];

  private static testTacticFields: IGeoTestMediaFieldDefinition[] = [
    {
      header: 'Tactic',
      field: 'tactic',
      order: 1,
      width: '50rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.custom
    },
    {
      header: 'Reference Spend',
      field: 'refSpend',
      order: 2,
      width: '8rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.currency
    },
    {
      header: 'Reference Contribution',
      field: 'refContribution',
      order: 3,
      width: '8rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.percent
    },
    {
      header: 'Campaigns',
      field: 'campaignCount',
      order: 4,
      width: '6rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.metadata
    },
    {
      header: '',
      field: 'removeTactic',
      order: 5,
      width: '4rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.action
    }
  ];

  private static testCampaignFields: IGeoTestMediaFieldDefinition[] = [
    {
      header: 'Campaign',
      field: 'campaignName',
      order: 1,
      width: '20rem',
      availableFor: [GeoTestBudgetLevel.campaign],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.custom
    },
    {
      header: 'Ad Set',
      field: 'subCampaignName',
      order: 1,
      width: '20rem',
      availableFor: [GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.custom
    },
    {
      header: 'Reference Spend',
      field: 'refSpend',
      order: 2,
      width: '8rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.currency
    },
    {
      header: 'Reference Contribution',
      field: 'refContribution',
      order: 3,
      width: '8rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.percent
    },
    {
      header: 'ID',
      field: 'campaignId',
      order: 4,
      width: '10rem',
      availableFor: [GeoTestBudgetLevel.campaign],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.metadata
    },
    {
      header: 'ID',
      field: 'subCampaignId',
      order: 4,
      width: '10rem',
      availableFor: [GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.metadata
    },
    {
      header: 'Daily Scale Budget',
      field: 'scaleBudget',
      order: 5,
      width: '8rem',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale],
      columnType: ColumnType.currency
    },
    {
      header: 'Included',
      field: 'includedInTest',
      order: 6,
      width: '6rem',
      styleClass: 'text-center',
      availableFor: [GeoTestBudgetLevel.campaign, GeoTestBudgetLevel.adset],
      forTypes: [GeoTestType.scale, GeoTestType.holdout, GeoTestType.lift],
      columnType: ColumnType.switch
    }
  ];

  public static testMetricDefinitions: IGeoTestMetricDefinition[] = [
    {
      metric: 'scaleCost',
      label: 'Scale Cost',
      heroMetricLabel: 'Scale Cost',
      digitsInfo: '1.0-0',
      type: 'currency',
      description:
        'The amount spent to scale the spending on the tactics in the test'
    },
    {
      metric: 'predictedOrders',
      label: 'Predicted Conversions',
      heroMetricLabel: 'Predicted Conversions',
      digitsInfo: '1.0-0',
      type: 'number',
      description:
        'Conversions projected to have taken place under normal conditions'
    },
    {
      metric: 'orders',
      label: 'Actual Conversions',
      heroMetricLabel: 'Actual Conversions',
      digitsInfo: '1.0-0',
      type: 'number',
      description:
        'Conversions that were recorded in test markets during the test'
    },
    {
      metric: 'ordersActual',
      label: 'Actual Conversions',
      heroMetricLabel: 'Actual Conversions',
      digitsInfo: '1.0-0',
      type: 'number',
      description:
        'Conversions that were recorded in test markets during the test'
    },
    {
      metric: 'contribution',
      label: 'Contribution',
      heroMetricLabel: 'Contribution',
      digitsInfo: '1.0-2',
      type: 'percent',
      description:
        'The percentage that the tested tactic is contributing to your overall business'
    },
    {
      metric: 'predictedSales',
      label: 'Predicted Revenue',
      heroMetricLabel: 'Predicted Revenue',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'Predicted conversions multiplied by AOV'
    },
    {
      metric: 'cpaI',
      label: 'Incremental CPO',
      heroMetricLabel: 'Incremental CPO',
      digitsInfo: '1.2-2',
      type: 'currency',
      description: 'Average cost for each incremental order'
    },
    {
      metric: 'roasI',
      label: 'Incremental ROAS',
      heroMetricLabel: 'Incremental ROAS',
      digitsInfo: '1.2-2',
      type: 'currency',
      description: 'Return on ad spending generated by your incremental revenue'
    },
    {
      metric: 'incrementality',
      label: 'Test Adjustment Factor',
      heroMetricLabel: 'Test Adjustment Factor',
      digitsInfo: '1.2-2',
      type: 'percent',
      description:
        'The factor applied to your reported orders to determine your incremental metrics'
    },
    {
      metric: 'revenue',
      label: 'Platform-Reported Revenue',
      heroMetricLabel: 'Platform-Reported Revenue',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'Revenue reported by your ad platform'
    },
    {
      metric: 'confidenceLevel',
      label: 'Confidence Rating',
      heroMetricLabel: 'Confidence Rating',
      digitsInfo: '1.0-0',
      type: 'percent',
      description: 'The confidence rating of the test'
    },
    {
      metric: 'testDurationInWeeks',
      label: 'Test Duration',
      heroMetricLabel: 'Test Duration',
      digitsInfo: '1.0-0',
      type: 'number',
      description: 'The duration of the test'
    },
    {
      metric: 'tactics',
      label: 'Tactics',
      heroMetricLabel: 'Tactics',
      digitsInfo: '1.0-0',
      type: 'text',
      description: 'The number of tactics in the test'
    },
    {
      metric: 'scaleFactor',
      label: 'Scale Factor',
      heroMetricLabel: 'Scale Factor',
      digitsInfo: '1.0-2',
      type: 'number',
      description: 'The scale factor of the test'
    },
    {
      metric: 'mediaSpend',
      label: 'Media Spend',
      heroMetricLabel: 'Media Spend',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'The amount spent on the tactics in the test'
    },
    {
      metric: 'marginalSpend',
      label: 'Scale Spend',
      heroMetricLabel: 'Scale Spend',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'Scale amount spent to reach significant lift'
    },
    {
      metric: 'normalSpend',
      label: 'Normal Spend',
      heroMetricLabel: 'Normal Spend',
      digitsInfo: '1.0-0',
      type: 'currency',
      description:
        'What you would have spent on test markets at normal spending levels'
    },
    {
      metric: 'totalSpend',
      label: 'Total Spend',
      heroMetricLabel: 'Total Spend',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'The total amount spent on the tactics in this test'
    },
    {
      metric: 'revenueI',
      label: 'Incremental Revenue',
      heroMetricLabel: 'Incremental Revenue',
      digitsInfo: '1.0-0',
      type: 'currency',
      description: 'Determined by multiplying incremental conversions by AOV'
    },
    {
      metric: 'ordersI',
      label: 'Incremental Conversions',
      heroMetricLabel: 'Lift (Incremental Conversions)',
      digitsInfo: '1.0-0',
      type: 'number',
      description:
        'Determined by multiplying total conversions from untested markets by the contribution percentage'
    },
    {
      metric: 'aov',
      label: 'AOV',
      heroMetricLabel: 'AOV',
      digitsInfo: '1.2-2',
      type: 'currency',
      description: 'The average monetary value of your orders'
    },
    {
      metric: 'conversions',
      label: 'Platform Reported Conversions',
      heroMetricLabel: 'Platform Reported Conversions',
      digitsInfo: '1.0-0',
      type: 'number',
      description: 'Conversions reported by your ad platform'
    }
  ];

  public static getTestNameIcons(): IColumnIcon {
    return {
      iconName: {
        [GeoCellDisplayStatus.resultsPending]: 'cached',
        [GeoCellDisplayStatus.actionNeeded]: 'warning'
      },
      iconStyleClass: {
        [GeoCellDisplayStatus.resultsPending]: 'text-muted',
        [GeoCellDisplayStatus.actionNeeded]: 'text-danger'
      },
      infoText: {
        [GeoCellDisplayStatus.resultsPending]: `
          <div class="text-start">
            <div class="b2 text-gray-500 mb-1"> Results pending </div>
            <div class="b1">It may take up to 7 days for results to be processed. We'll keep you posted.</div>
          </div>`,
        [GeoCellDisplayStatus.actionNeeded]: `
           <div class="text-start">
              <div class="b2 text-red-400 mb-1"> Approval is needed for launch </div>
              <div class="b1">The parameters of your test have changed due to the tactic's recent performance.</div>
           </div>`
      }
    };
  }

  public static getGeoTestColumnsForStatus(status: GeoTestStatus) {
    return sortBy(
      this.geoTestsFieldDefinition.filter((column) =>
        column.availableFor.includes(status)
      ),
      ['order']
    );
  }

  public static getRecommendedTestColumns(
    fieldIds: string[]
  ): IGeoTestColumnDefinition[] {
    const filteredColumns = sortBy(
      this.recommendedTestsFieldDefinition.filter(
        (columnDef) => isEmpty(fieldIds) || fieldIds.includes(columnDef.field)
      ),
      ['order']
    );

    if (!isEmpty(fieldIds)) {
      return filteredColumns.sort(
        (columnDef1, columnDef2) =>
          fieldIds.indexOf(columnDef1.field) -
          fieldIds.indexOf(columnDef2.field)
      );
    }
    return filteredColumns;
  }

  public static getAffectedCampaignColumns(
    level: GeoTestBudgetLevel,
    testType: GeoTestType
  ): IGeoTestMediaFieldDefinition[] {
    return GeoTestsFieldDefinition.testCampaignFields.filter(
      (field) =>
        field.availableFor.includes(level) && field.forTypes.includes(testType)
    );
  }

  public static getAffectedTacticColumns(
    level: GeoTestBudgetLevel,
    testType: GeoTestType,
    removeTacticAllowed: boolean
  ): IGeoTestMediaFieldDefinition[] {
    const tacticColumns = GeoTestsFieldDefinition.testTacticFields.filter(
      (field) =>
        field.availableFor.includes(level) && field.forTypes.includes(testType)
    );
    if (removeTacticAllowed) {
      return tacticColumns;
    }
    return tacticColumns.filter(
      (column) => column.columnType !== ColumnType.action
    );
  }
}
